import type { DisciplineType, UserRole, ScopeType, ActivityStatus } from '@/types'

// =============================================================================
// PROGRESS COLORS
// =============================================================================

export const PROGRESS_THRESHOLDS = [
  { min: 0,   max: 0,   color: '#9CA3AF', label: 'No iniciada' },
  { min: 0,   max: 25,  color: '#FACC15', label: '1-24%' },
  { min: 25,  max: 50,  color: '#FB923C', label: '25-49%' },
  { min: 50,  max: 75,  color: '#4ADE80', label: '50-74%' },
  { min: 75,  max: 100, color: '#16A34A', label: '75-99%' },
  { min: 100, max: 100, color: '#2563EB', label: 'Completada' },
] as const

export const PROGRESS_COLORS: Record<number, string> = {
  0:   '#9CA3AF',
  1:   '#FACC15',
  25:  '#FB923C',
  50:  '#4ADE80',
  75:  '#16A34A',
  100: '#2563EB',
}

export const STATUS_COLORS: Record<string, string> = {
  NO_INICIADA:          '#9CA3AF',
  PLANIFICADA:          '#D1D5DB',
  EN_CURSO:             '#FACC15',
  FINALIZADA:           '#2563EB',
  BLOQUEADA:            '#DC2626',
  PENDIENTE_DOCUMENTAL: '#7C3AED',
}

export function getProgressColor(pct: number): string {
  if (pct <= 0)   return '#9CA3AF'
  if (pct < 25)   return '#FACC15'
  if (pct < 50)   return '#FB923C'
  if (pct < 75)   return '#4ADE80'
  if (pct < 100)  return '#16A34A'
  return '#2563EB'
}

export function getStatusColor(status: ActivityStatus): string {
  return STATUS_COLORS[status] ?? '#9CA3AF'
}

// =============================================================================
// VALIDATION COLORS
// =============================================================================

export const VALIDATION_COLORS: Record<string, string> = {
  PENDIENTE:   '#FACC15',
  APROBADO:    '#16A34A',
  RECHAZADO:   '#DC2626',
  EN_REVISION: '#FB923C',
}

export const DOCUMENT_STATUS_COLORS: Record<string, string> = {
  PENDIENTE:   '#9CA3AF',
  EN_REVISION: '#FACC15',
  APROBADO:    '#16A34A',
  RECHAZADO:   '#DC2626',
}

// =============================================================================
// PARCELAS
// =============================================================================

export const PARCELAS = Array.from({ length: 26 }, (_, i) => ({
  number:      i + 1,
  name:        `Parc-${String(i + 1).padStart(2, '0')}`,
  displayName: `Parcela ${String(i + 1).padStart(2, '0')}`,
  hasSubestacion:         i + 1 === 7 || i + 1 === 21,
  hasInstalacionTemporal: i + 1 === 5 || i + 1 === 21 || i + 1 === 25,
}))

// =============================================================================
// SCOPE TYPES
// =============================================================================

export const SCOPE_TYPE_LABELS: Record<ScopeType, string> = {
  PARCELA:                 'Parcela',
  GLOBAL:                  'Global',
  SUBESTACION:             'Subestación',
  INSTALACION_TEMPORAL:    'Instalación Temporal',
  LINEA_132KV:             'Línea 132kV',
  LINEA_400KV:             'Línea 400kV',
  CAMARA_EMPALME_BOTELLA:  'Cámara Empalme/Botellas',
  PERFORACION_HORIZONTAL:  'Perforación Horizontal',
  EDIFICIO:                'Edificio',
  TRACKER:                 'Tracker',
  HINCA:                   'Hinca',
  ZANJA_BT:                'Zanja BT',
  ZANJA_MT:                'Zanja MT',
  CCTV:                    'CCTV',
  VIAL:                    'Vial',
  VALLADO:                 'Vallado',
  EQUIPO:                  'Equipo',
}

export const SCOPE_TYPES: { value: ScopeType; label: string }[] = (
  Object.entries(SCOPE_TYPE_LABELS) as [ScopeType, string][]
).map(([value, label]) => ({ value, label }))

// =============================================================================
// DISCIPLINES
// =============================================================================

export interface DisciplineInfo {
  value:  DisciplineType
  label:  string
  color:  string
  bgColor: string
}

export const DISCIPLINES: DisciplineInfo[] = [
  { value: 'Civil',                  label: 'Civil',                   color: '#78716C', bgColor: '#F5F5F4' },
  { value: 'Mecanica',               label: 'Mecánica',                color: '#0EA5E9', bgColor: '#F0F9FF' },
  { value: 'Electrica_BT',           label: 'Eléctrica BT',            color: '#F59E0B', bgColor: '#FFFBEB' },
  { value: 'Electrica_MT',           label: 'Eléctrica MT',            color: '#F97316', bgColor: '#FFF7ED' },
  { value: 'Alta_Tension',           label: 'Alta Tensión',            color: '#DC2626', bgColor: '#FEF2F2' },
  { value: 'Modulos',                label: 'Módulos',                 color: '#16A34A', bgColor: '#F0FDF4' },
  { value: 'CCTV_Seguridad',         label: 'CCTV/Seguridad',          color: '#7C3AED', bgColor: '#F5F3FF' },
  { value: 'Subestaciones',          label: 'Subestaciones',           color: '#DB2777', bgColor: '#FDF2F8' },
  { value: 'Lineas_Evacuacion',      label: 'Líneas Evacuación',       color: '#0284C7', bgColor: '#F0F9FF' },
  { value: 'Instalaciones_Temporales', label: 'Instalaciones Temp.',   color: '#6B7280', bgColor: '#F9FAFB' },
  { value: 'Edificios',              label: 'Edificios',               color: '#92400E', bgColor: '#FFFBEB' },
]

export const DISCIPLINE_MAP: Record<DisciplineType, DisciplineInfo> = Object.fromEntries(
  DISCIPLINES.map(d => [d.value, d])
) as Record<DisciplineType, DisciplineInfo>

export function getDisciplineInfo(discipline: DisciplineType): DisciplineInfo {
  return DISCIPLINE_MAP[discipline] ?? DISCIPLINES[0]
}

// =============================================================================
// USER ROLES
// =============================================================================

export interface RoleInfo {
  value:       UserRole
  label:       string
  shortLabel:  string
  color:       string
  permissions: {
    canViewAll:           boolean
    canEdit:              boolean
    canValidate:          boolean
    canUploadDocuments:   boolean
    canManageUsers:       boolean
    canManageCompanies:   boolean
    canExport:            boolean
  }
}

export const ROLES: RoleInfo[] = [
  {
    value:      'ADMIN',
    label:      'Administrador',
    shortLabel: 'Admin',
    color:      '#7C3AED',
    permissions: {
      canViewAll: true, canEdit: true, canValidate: true,
      canUploadDocuments: true, canManageUsers: true,
      canManageCompanies: true, canExport: true,
    },
  },
  {
    value:      'SUPERVISOR_COBRA',
    label:      'Supervisor COBRA',
    shortLabel: 'Supervisor',
    color:      '#2563EB',
    permissions: {
      canViewAll: true, canEdit: true, canValidate: true,
      canUploadDocuments: true, canManageUsers: false,
      canManageCompanies: false, canExport: true,
    },
  },
  {
    value:      'SUBCONTRATISTA',
    label:      'Subcontratista',
    shortLabel: 'Subcontr.',
    color:      '#059669',
    permissions: {
      canViewAll: false, canEdit: false, canValidate: false,
      canUploadDocuments: true, canManageUsers: false,
      canManageCompanies: false, canExport: false,
    },
  },
  {
    value:      'CONSULTA_CLIENTE',
    label:      'Consulta Cliente',
    shortLabel: 'Consulta',
    color:      '#6B7280',
    permissions: {
      canViewAll: false, canEdit: false, canValidate: false,
      canUploadDocuments: false, canManageUsers: false,
      canManageCompanies: false, canExport: false,
    },
  },
]

export const ROLE_MAP: Record<UserRole, RoleInfo> = Object.fromEntries(
  ROLES.map(r => [r.value, r])
) as Record<UserRole, RoleInfo>

export function getRoleInfo(role: UserRole): RoleInfo {
  return ROLE_MAP[role] ?? ROLES[3]
}

// =============================================================================
// ACTIVITY FAMILY CODES
// =============================================================================

export const ACTIVITY_FAMILY_NAMES: Record<string, string> = {
  '1':  'Instalaciones temporales',
  '2':  'Hincado',
  '3':  'Montaje de tracker',
  '4':  'Montaje módulos',
  '5':  'Zanjas BT',
  '6':  'Zanjas MT',
  '7':  'Zanjas 132kV',
  '8':  'Prisma línea 400kV',
  '9':  'Cámaras empalme y botellas',
  '10': 'Instalación equipos',
  '11': 'Conexionado',
  '12': 'CCTV',
  '13': 'Perforaciones horizontales',
  '14': 'Vallado',
  '15': 'Viales',
  '16': 'Subestaciones',
  '17': 'Edificios',
}

// =============================================================================
// DOCUMENT TYPES
// =============================================================================

export const DOCUMENT_TYPE_LABELS: Record<string, string> = {
  PPI:         'Plan de Punto de Inspección',
  CHECKLIST:   'Lista de Verificación',
  FOTO:        'Registro Fotográfico',
  PLANO:       'Plano As-Built',
  CERTIFICADO: 'Certificado / Ensayo',
  INFORME:     'Informe de Avance',
  OTRO:        'Otro Documento',
}

// =============================================================================
// ZONES
// =============================================================================

export const ZONE_LABELS: Record<string, string> = {
  CORTAS:  'Cortas',
  GUARDAS: 'Guardas',
  PALOMAR: 'Palomar',
}

export const ZONE_COLORS: Record<string, string> = {
  CORTAS:  '#3B82F6',
  GUARDAS: '#10B981',
  PALOMAR: '#F59E0B',
}

// =============================================================================
// MAP DEFAULTS
// =============================================================================

// Center of Navabuena plant — approximate UTM 30N centroid in WGS84
export const MAP_DEFAULT_CENTER: [number, number] = [-3.85, 41.85]
export const MAP_DEFAULT_ZOOM = 13

export const MAP_STYLE_FREE = 'https://demotiles.maplibre.org/style.json'

// =============================================================================
// MISC
// =============================================================================

export const APP_NAME    = 'PSFV Navabuena'
export const APP_VERSION = '1.0.0'

export const DEBOUNCE_MS   = 300
export const STALE_TIME_MS = 5 * 60 * 1000   // 5 minutes
export const CACHE_TIME_MS = 10 * 60 * 1000  // 10 minutes
